import {
  EvaluationResultByIdQuery,
  EvaluationResultIdByUUIDQuery,
  PolicyGql,
} from '@agilelab/plugin-wb-governance-common';
import { useApolloClient } from '@apollo/client';
import { ErrorPage, ErrorPanel, Progress } from '@backstage/core-components';
import { Box } from '@material-ui/core';
import { FC, useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { toEvaluationResultSpecific } from '../../mappers/to-evaluation-result-specific.mapper';
import { GovernanceTestOverviewMetric } from '../GovernanceTestPage/GovernanceTestOverview/Metric/OverviewMetricContent';
import { GovernanceTestOverviewPolicy } from '../GovernanceTestPage/GovernanceTestOverview/Policy/OverviewPolicyContent';
import {
  GET_EVALUATION_RESULT_BY_ID,
  GET_EVALUATION_RESULT_BY_UUID,
} from './queries/evaluation-result-by-id.query';
import { useEvaluationResultTitle } from '../../hooks/useValuationResultTitle';
import {
  MetricHeader,
  PolicyHeader,
} from '../GovernanceTestPage/GovernanceTestOverview';

export const GovernanceSpecificResultComponent: FC<{
  evaluationId: number | string;
  idType?: 'pk' | 'external';
  showHeader?: boolean;
}> = ({ evaluationId, idType = 'pk', showHeader = true }) => {
  const apolloClient = useApolloClient();

  const {
    value: data,
    error,
    loading,
  } = useAsync(async () => {
    if (idType === 'external') {
      const res = await apolloClient.query<EvaluationResultIdByUUIDQuery>({
        query: GET_EVALUATION_RESULT_BY_UUID,
        variables: { uuid: evaluationId },
      });
      return {
        cgp_evaluation_result_by_pk: res.data.cgp_evaluation_result[0],
      };
    }
    const res = await apolloClient.query<EvaluationResultByIdQuery>({
      query: GET_EVALUATION_RESULT_BY_ID,
      variables: { resultId: evaluationId },
    });
    return {
      cgp_evaluation_result_by_pk: res.data.cgp_evaluation_result_by_pk,
    };
  });

  const notFound = useMemo(
    () => data && data.cgp_evaluation_result_by_pk === null,
    [data],
  );

  const evaluationResultSpecific = useMemo(
    () =>
      data &&
      data.cgp_evaluation_result_by_pk &&
      toEvaluationResultSpecific(data),
    [data],
  );

  const resultTitle = useEvaluationResultTitle(evaluationResultSpecific);

  if (error) {
    return <ErrorPanel error={error} />;
  }

  if (loading) {
    return <Progress />;
  }

  if (notFound) {
    return (
      <ErrorPage
        status="404"
        statusMessage="Result not found"
        additionalInfo={`No result found with ID ${evaluationId}`}
      />
    );
  }

  return (
    <Box>
      {evaluationResultSpecific?.truePolicy && (
        <>
          {showHeader && (
            <PolicyHeader
              name={resultTitle}
              task={{
                status: evaluationResultSpecific?.outcome,
                severity: (
                  evaluationResultSpecific?.governance_entity as PolicyGql
                ).content.severity,
                policyStatus:
                  evaluationResultSpecific?.governance_entity.status,
              }}
            />
          )}
          <GovernanceTestOverviewPolicy
            kind="tabs"
            policy={evaluationResultSpecific.truePolicy}
            result={evaluationResultSpecific}
          />
        </>
      )}

      {evaluationResultSpecific?.metric && (
        <>
          {showHeader && (
            <MetricHeader
              name={resultTitle}
              task={{
                value: evaluationResultSpecific?.result?.value || 0,
                hasError: evaluationResultSpecific?.hasError,
                thresholdResult:
                  evaluationResultSpecific?.result?.thresholdResult,
              }}
            />
          )}
          <GovernanceTestOverviewMetric
            kind="tabs"
            metric={evaluationResultSpecific.metric}
            result={evaluationResultSpecific}
          />
        </>
      )}
    </Box>
  );
};
